import _ from 'lodash';
import type { z } from 'zod';
import { LegacyExamAttemptStatus, LegacyExamAttemptType, type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import { mergeMissedMarks } from '../legacyExamAttempts/utils';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBLegacyExamQuestionAttemptSchema, LegacyExamQuestionAttemptSchema } from './schemas';

type DBLegacyExamQuestionAttempt = z.infer<typeof DBLegacyExamQuestionAttemptSchema>;
export const LegacyExamQuestionAttemptCodecConfig: TransformerConfig<
  typeof LegacyExamQuestionAttemptSchema,
  typeof DBLegacyExamQuestionAttemptSchema
> = {
  name: 'LegacyExamQuestionAttempt',
  clientSchema: LegacyExamQuestionAttemptSchema,
  dbSchema: DBLegacyExamQuestionAttemptSchema,
  transformers: [
    // examSubject, examId, examAttemptNumber, questionId ↔ subjectExamQuestionIdentifier, exam
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      encoder: {
        sourceFields: ['examSubject', 'examId', 'examAttemptNumber', 'questionId'] as const,
        encode: ({ examSubject, examId, examAttemptNumber, questionId }) => ({
          exam: `${examSubject}#${examId}`,
          subjectExamQuestionIdentifier: `${LegacyExamAttemptType.Question}#${examSubject}#${examId}#${examAttemptNumber}#${questionId}`,
        }),
      },
      decoder: {
        sourceFields: ['subjectExamQuestionIdentifier', 'exam'] as const,
        decode: ({ subjectExamQuestionIdentifier }) => {
          const identifier =
            subjectExamQuestionIdentifier as DBLegacyExamQuestionAttempt['subjectExamQuestionIdentifier'];
          const parts = identifier.split('#');
          return {
            examSubject: parts[1] as Subject,
            examId: parts[2] as string,
            examAttemptNumber: _.toInteger(parts[3]),
            questionId: parts[4] as string,
          };
        },
      },
    }),
    // Merge missedMarks into inaccuracyMarks on decode
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      decoder: {
        sourceFields: ['inaccuracyMarks', 'missedMarks'] as const,
        decode: data =>
          mergeMissedMarks(data as { inaccuracyMarks?: number; missedMarks?: number }),
      },
    }),
    // status: LegacyExamAttemptStatus ↔ status: 'QUESTION#status'
    createTransformer<
      typeof LegacyExamQuestionAttemptSchema,
      typeof DBLegacyExamQuestionAttemptSchema
    >({
      encoder: {
        sourceFields: ['status'] as const,
        encode: ({ status }) => ({
          status: `${LegacyExamAttemptType.Question}#${status}`,
        }),
      },
      decoder: {
        sourceFields: ['status'] as const,
        decode: ({ status }) => ({
          status: (status as DBLegacyExamQuestionAttempt['status']).split(
            '#'
          )[1] as LegacyExamAttemptStatus,
        }),
      },
    }),
  ],
};

export const encodeLegacyExamQuestionAttemptToDb = createEncodeToDbFn(
  LegacyExamQuestionAttemptCodecConfig
);
export const decodeLegacyExamQuestionAttemptFromDb = createDecodeFromDbFn(
  LegacyExamQuestionAttemptCodecConfig
);
